import { useCallback, useEffect, useMemo, useState } from 'react';
import Hammer from 'react-hammerjs';
import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'hammerjs';

import {
  DROP_ITEM_SIZE,
  InteractiveSequenceAttempt,
  ViewProps,
} from '../settings/interactive-response.constants';
import { InteractiveDrag } from '../../../shared/interactive-drag-drop/interactive-drag';
import { isEveryDraggableDropped } from '../../../shared/interactive-drag-drop/interactive-drag-drop.utils';
import {
  CANCELLED,
  INTERACTIVE_CONTAINER_ID,
  PLACED,
} from '../../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import InteractiveDragDrop from '../../../shared/interactive-drag-drop/interactive-drag-drop';
import { useInteractiveResponsePlay } from './interactive-response-sequence.hooks';
import * as Styled from './interactive-response-sequence.style';
import {
  InteractiveResponseItemCount,
  InteractiveResponseScreenReader,
  InteractiveResponseTitle,
} from '../../shared/interactive-response-shared';
import { useTileScroll } from '../../shared/interactive-response-shared.hooks';
import {
  InteractiveCanvas,
  InteractiveResponseModalControlsWrapper,
  InteractiveResponsePlayContainer,
  InteractiveResponseScrollableList,
  InteractiveResponseScrollableWrapper,
} from '../../shared/interactive-response-shared.style';
import { CheckAnswerSmall } from '../../shared/interactive-response-check-answer';
import {
  getResults,
  initialise,
  populateAnswers,
  resetIncorrectItems,
} from './interactive-response-sequence.utils';
import { InteractiveSequenceDrop } from './interactive-sequence-drop';
import {
  isEverythingCorrect,
  returnItemToOriginalPosition,
} from '../../shared/interactive-response-shared.utils';
import {
  InteractiveResponseHelpControls,
  InteractiveResponseNavMobileControls,
} from '../../shared/interactive-response-controls';
import { FlexLeftAlign } from 'approot/shared/styles/flex.style';
import type { InteractiveResponseAnswerFragment } from '../../../../present/activity-answers.graphql.generated';
import {
  InteractiveAudioProvider,
  useInteractiveAudioContext,
} from '../../shared/interactive-audio.context';

const InteractiveResponseSequenceSmall = ({
  properties,
  playClick,
  playStart,
  playAudio,
  onAnswerAttempt,
  attemptAnswers,
  toggleAudio,
}: ViewProps & {
  onClose: () => void;
  playClick: () => void;
  playStart: () => void;
  toggleAudio: () => void;
  playAudio: boolean;
  onAnswerAttempt: InteractiveSequenceAttempt;
  attemptAnswers?: InteractiveResponseAnswerFragment;
}) => {
  const [scrollRef, setScrollRef] = useState<HTMLElement | null>(null);
  const [hasCheckedAnswer, setHasCheckedAnswer] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);

  const sequenceProperties = properties.interactiveResponse;

  const isCheckAnswerAtEnd =
    sequenceProperties.checkAnswerAtEnd === null
      ? true
      : sequenceProperties.checkAnswerAtEnd;

  const { setAudioList } = useInteractiveAudioContext();

  const {
    currentSlide,
    prevDisabled,
    nextDisabled,
    handleNext,
    handlePrev,
    reset,
  } = useTileScroll({
    containerRef: scrollRef,
    itemWidth: 122,
    slideCount: sequenceProperties.items.length,
  });

  const {
    playAttempt,
    setPlayAttempt,
    dropItems,
    setDropItems,
    dragItems,
    setDragItems,
    keyboardState,
    handleOnDrop,
    handleKeyboardInteraction,
    handleCheckAnswers,
  } = useInteractiveResponsePlay({
    playClick,
    isCheckAnswerAtEnd,
    onAnswerAttempt,
  });

  const initialiseItems = useCallback(
    (startAgain?: boolean) => {
      const { dragItems, dropItems } =
        attemptAnswers && !startAgain
          ? populateAnswers(
              attemptAnswers,
              sequenceProperties.items,
              sequenceProperties.variant
            )
          : initialise(
              sequenceProperties.variant,
              sequenceProperties.items,
              true
            );
      setDropItems(dropItems);
      setDragItems(dragItems);
      setPlayAttempt(playAttempt + 1);
      setHasCheckedAnswer(!!attemptAnswers && !startAgain);
      reset();
    },
    [
      sequenceProperties.variant,
      sequenceProperties.items,
      playAttempt,
      setPlayAttempt,
      setDragItems,
      setDropItems,
      setHasCheckedAnswer,
      reset,
      attemptAnswers,
    ]
  );

  const setup = useCallback(
    (startAgain?: boolean) => {
      setTimeout(() => {
        initialiseItems(startAgain);
        playStart();
      }, 100);
    },
    [initialiseItems, playStart]
  );

  const tryAgain = useCallback(() => {
    const { dragItems: updatedDragItems, dropItems: updatedDropItems } =
      resetIncorrectItems(dragItems, dropItems);

    setDragItems(updatedDragItems);
    setDropItems(updatedDropItems);
    setHasCheckedAnswer(false);

    setTimeout(() => {
      reset();
      playStart();
    }, 100);
  }, [
    dragItems,
    dropItems,
    setDragItems,
    setDropItems,
    setHasCheckedAnswer,
    reset,
    playStart,
  ]);

  const isAnswered = useMemo(() => {
    return isEveryDraggableDropped(dragItems);
  }, [dragItems]);

  const keyboardProps = {
    target:
      keyboardState?.action === CANCELLED || keyboardState?.action === PLACED
        ? undefined
        : keyboardState?.id,
    onKeyboardInteraction: handleKeyboardInteraction,
  };

  useEffect(() => {
    initialiseItems();
    //eslint-disable-next-line
  }, [properties, attemptAnswers]);

  useEffect(() => {
    setAudioList(
      sequenceProperties.items.map(item => ({
        itemId: item.itemId,
        audioUrl: item.audio?.original,
      }))
    );
  }, [setAudioList, sequenceProperties.items]);

  return (
    <InteractiveResponsePlayContainer>
      <InteractiveCanvas
        id={INTERACTIVE_CONTAINER_ID}
        data-selector="interactive-sequence-window"
      >
        <FlexLeftAlign $marginBottom={8}>
          <InteractiveResponseHelpControls
            howToPlay={sequenceProperties.howToPlay}
            toggleAudio={toggleAudio}
            playAudio={playAudio}
          />
        </FlexLeftAlign>
        <InteractiveResponseTitle
          title={sequenceProperties.title}
          subTitle={sequenceProperties.subTitle}
        />

        <Hammer
          options={{
            recognizers: {
              swipe: { enable: true },
            },
          }}
          onSwipe={arg => {
            if (arg.direction === DIRECTION_LEFT && !prevDisabled) {
              handlePrev();
            } else if (arg.direction === DIRECTION_RIGHT && !nextDisabled) {
              handleNext();
            }
          }}
        >
          <InteractiveResponseScrollableWrapper>
            <InteractiveResponseScrollableList ref={setScrollRef}>
              {dragItems.map((dragItem, index) => (
                <Styled.InteractiveResponseScrollableItem
                  id={dragItem.id}
                  key={`${playAttempt}-${dragItem.id}-${index}-${playAudio}`}
                  $isInFocus={currentSlide === index}
                  $index={index}
                  data-selector={`sequence-image-${index}`}
                >
                  {!dragItem.isDropped && (
                    <InteractiveDrag
                      id={dragItem.id}
                      imageUrl={dragItem.question.image?.small}
                      isFit={dragItem.question.image?.isFit}
                      description={dragItem.question.image?.description || ''}
                      baseColour={dragItem.question.baseColour}
                      variant={dragItem.question.variant}
                      text={dragItem.question.text}
                      richtext={dragItem.question.richtext}
                      audioUrl={dragItem.question.audioUrl}
                      keyboard={keyboardProps}
                    />
                  )}
                </Styled.InteractiveResponseScrollableItem>
              ))}
            </InteractiveResponseScrollableList>
          </InteractiveResponseScrollableWrapper>
        </Hammer>

        <InteractiveResponseItemCount
          index={currentSlide}
          total={sequenceProperties.items.length}
        />

        {isAnswered && (hasCheckedAnswer || !isCheckAnswerAtEnd) && (
          <CheckAnswerSmall
            isActive={isActive}
            setIsActive={setIsActive}
            result={getResults(dragItems, dropItems)}
            isEverythingCorrect={isEverythingCorrect(dropItems, dragItems)}
            moreInformation={sequenceProperties.moreInformation}
            onStartOver={() => {
              setup(true);
            }}
            onTryAgain={tryAgain}
          />
        )}

        <Styled.InteractiveResponseDropItemContainer>
          {dropItems.map(dropItem => {
            const zone = dropItem.zones[0];
            const droppedDragItem = dragItems.find(
              d => d.id === zone.dragItemId || d.id === zone.previewItemId
            );

            return (
              <InteractiveSequenceDrop
                key={`${playAttempt}-${dropItem.id}-${playAudio}`}
                id={dropItem.id}
                label={dropItem.label}
                labelLength={sequenceProperties.labelLength}
                labelBorder={sequenceProperties.labelBorder}
                isCorrect={zone.isCorrect}
                dropItems={dropItems}
                dragItems={dragItems}
                onDrop={(destinationId, sourceId) => {
                  handleOnDrop(destinationId, sourceId);
                }}
                isContinuousMarking={!isCheckAnswerAtEnd}
                onIncorrectAnswer={element =>
                  returnItemToOriginalPosition({
                    element,
                    droppedDragItemId: droppedDragItem?.id || '',
                    scale: 1,
                    dropItem,
                    dropItems,
                    dragItems,
                    setDragItems,
                    setDropItems,
                  })
                }
              >
                {droppedDragItem && (
                  <InteractiveDrag
                    id={droppedDragItem.id}
                    imageUrl={droppedDragItem.question.image?.small}
                    isFit={droppedDragItem.question.image?.isFit}
                    description={
                      droppedDragItem.question.image?.description || ''
                    }
                    keyboard={keyboardProps}
                    baseColour={droppedDragItem.question.baseColour}
                    variant={droppedDragItem.question.variant}
                    text={droppedDragItem.question.text}
                    richtext={droppedDragItem.question.richtext}
                    audioUrl={droppedDragItem.question.audioUrl}
                    isDisabled={zone.isCorrect !== undefined}
                    isCorrect={zone.isCorrect}
                    droppedAt={zone.dragItemId ? dropItem.label : ''}
                    overAt={zone.previewItemId ? dropItem.label : ''}
                    scaleTo={DROP_ITEM_SIZE}
                  />
                )}
              </InteractiveSequenceDrop>
            );
          })}
        </Styled.InteractiveResponseDropItemContainer>

        <InteractiveResponseScreenReader keyboardState={keyboardState} />
      </InteractiveCanvas>
      <InteractiveResponseModalControlsWrapper>
        <InteractiveResponseNavMobileControls
          onNext={handleNext}
          onPrevious={handlePrev}
          onCheckAnswer={() => {
            setHasCheckedAnswer(true);
            setIsActive(true);
            handleCheckAnswers();
          }}
          canCheckAnswer={isCheckAnswerAtEnd && isAnswered && !hasCheckedAnswer}
          isNextDisabled={nextDisabled}
          isPrevDisabled={prevDisabled}
        />
      </InteractiveResponseModalControlsWrapper>
    </InteractiveResponsePlayContainer>
  );
};

const InteractiveResponseSequenceSmallWrapper = (
  props: ViewProps & {
    onClose: () => void;
    playClick: () => void;
    playStart: () => void;
    playAudio: boolean;
    toggleAudio: () => void;
    attemptAnswers?: InteractiveResponseAnswerFragment;
    onAnswerAttempt: InteractiveSequenceAttempt;
  }
) => {
  return (
    <InteractiveAudioProvider>
      <InteractiveDragDrop>
        <InteractiveResponseSequenceSmall {...props} />
      </InteractiveDragDrop>
    </InteractiveAudioProvider>
  );
};

export default InteractiveResponseSequenceSmallWrapper;
